import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Search, Edit2, Trash2, Check, X, Loader2, Plus,
  CheckCircle, XCircle, Clock, Eye, Image as ImageIcon, Save
} from 'lucide-react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: API + '/api', headers: { Authorization: `Bearer ${token}` } });
};

const SuperAdminCatalog = () => {
  const [tab, setTab] = useState('catalog');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  // Edit modal
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [tab]);

  const fetchData = async () => {
    setLoading(true);
    const api = getApi();
    try {
      if (tab === 'catalog') {
        const res = await api.get('/admin/catalog');
        setProducts(res.data.products);
        setCategories(res.data.categories);
      } else {
        const res = await api.get('/admin/catalog/submissions?status=pending');
        setSubmissions(res.data.submissions);
      }
    } catch (e) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const handleSeed = async () => {
    const api = getApi();
    try {
      const res = await api.post('/admin/catalog/seed');
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error('Seed failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product from the global catalog?')) return;
    const api = getApi();
    try {
      await api.delete(`/admin/catalog/${id}`);
      toast.success('Deleted');
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e) { toast.error('Delete failed'); }
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setEditForm({
      name_es: p.name_es || '', name_en: p.name_en || '',
      category_es: p.category_es || '', category_en: p.category_en || '',
      price: p.price || '', cost_price: p.cost_price || '',
      barcode: p.barcode || '', tax_rate: p.tax_rate || '',
      unit: p.unit || '', image_url: p.image_url || '', description: p.description || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    const api = getApi();
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          payload[k] = k === 'price' || k === 'cost_price' ? parseFloat(v) : v;
        }
      });
      await api.put(`/admin/catalog/${editProduct.id}`, payload);
      toast.success('Product updated');
      setEditProduct(null);
      fetchData();
    } catch (e) { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (sub) => {
    const api = getApi();
    try {
      await api.post(`/admin/catalog/submissions/${sub.id}/approve`);
      toast.success('Approved and added to catalog');
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
    } catch (e) { toast.error('Approve failed'); }
  };

  const handleReject = async (sub) => {
    const api = getApi();
    try {
      await api.post(`/admin/catalog/submissions/${sub.id}/reject`);
      toast.success('Rejected');
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
    } catch (e) { toast.error('Reject failed'); }
  };

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name_en?.toLowerCase().includes(q) || p.name_es?.toLowerCase().includes(q) || p.barcode?.includes(q);
    const matchCat = !filterCat || p.category_en === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="sad-catalog-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Global Product Catalog</h1>
            <p className="text-slate-400 text-sm">{products.length} products in master catalog</p>
          </div>
          <button onClick={handleSeed}
            className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600"
            data-testid="seed-catalog-btn">
            Sync from JSON
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-0">
          <button onClick={() => setTab('catalog')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'catalog' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-white'
            }`} data-testid="tab-catalog">
            <Package className="w-4 h-4 inline mr-2" /> Catalog ({products.length})
          </button>
          <button onClick={() => setTab('submissions')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'submissions' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-white'
            }`} data-testid="tab-submissions">
            <Clock className="w-4 h-4 inline mr-2" /> Submissions ({submissions.length})
          </button>
        </div>

        {/* Catalog Tab */}
        {tab === 'catalog' && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                  data-testid="catalog-search" />
              </div>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="catalog-category-filter">
                <option value="">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
            ) : (
              <div className="bg-slate-800/50 border border-slate-700 rounded overflow-hidden">
                <table className="w-full text-sm" data-testid="catalog-table">
                  <thead>
                    <tr className="border-b border-slate-700 text-left">
                      <th className="px-4 py-3 text-slate-400 font-medium">Image</th>
                      <th className="px-4 py-3 text-slate-400 font-medium">Name (EN)</th>
                      <th className="px-4 py-3 text-slate-400 font-medium">Name (ES)</th>
                      <th className="px-4 py-3 text-slate-400 font-medium">Category</th>
                      <th className="px-4 py-3 text-slate-400 font-medium">Price</th>
                      <th className="px-4 py-3 text-slate-400 font-medium">Barcode</th>
                      <th className="px-4 py-3 text-slate-400 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id} className="border-b border-slate-700/50 hover:bg-slate-800/80" data-testid={`catalog-row-${p.id}`}>
                        <td className="px-4 py-2.5">
                          {p.image_url ? (
                            <img src={p.image_url.startsWith('/api') ? `${API}${p.image_url}` : p.image_url}
                              alt="" className="w-8 h-8 object-contain rounded bg-slate-700" />
                          ) : (
                            <div className="w-8 h-8 bg-slate-700 rounded flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-slate-500" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-white font-medium">{p.name_en}</td>
                        <td className="px-4 py-2.5 text-slate-300">{p.name_es}</td>
                        <td className="px-4 py-2.5 text-slate-400">{p.category_en}</td>
                        <td className="px-4 py-2.5 text-amber-400 font-medium">{'\u20AC'}{p.price?.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{p.barcode || '-'}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-amber-400"
                              data-testid={`catalog-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                              data-testid={`catalog-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div className="py-12 text-center text-slate-500">No products found</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Submissions Tab */}
        {tab === 'submissions' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
            ) : submissions.length === 0 ? (
              <div className="bg-slate-800/50 border border-slate-700 rounded p-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500/50" />
                <p className="text-slate-400">No pending submissions</p>
                <p className="text-slate-500 text-sm mt-1">Customer product submissions will appear here for review</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map(sub => (
                  <div key={sub.id} className="bg-slate-800/50 border border-slate-700 rounded p-4 flex items-center gap-4"
                    data-testid={`submission-${sub.id}`}>
                    <div className="w-10 h-10 bg-amber-500/10 rounded flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{sub.name}</p>
                      <p className="text-slate-400 text-xs">
                        {sub.category} &middot; {'\u20AC'}{sub.price?.toFixed(2)} &middot;
                        From: <span className="text-amber-400">{sub.shop_name}</span>
                        {sub.barcode && <> &middot; <span className="font-mono">{sub.barcode}</span></>}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => handleApprove(sub)}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
                        data-testid={`approve-${sub.id}`}>
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => handleReject(sub)}
                        className="px-3 py-1.5 bg-slate-700 text-slate-300 text-sm rounded hover:bg-red-600 hover:text-white flex items-center gap-1"
                        data-testid={`reject-${sub.id}`}>
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editProduct && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              onClick={() => setEditProduct(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-[#1e293b] border border-slate-700 rounded-lg flex flex-col max-h-[85vh]"
                onClick={e => e.stopPropagation()} data-testid="catalog-edit-modal">
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white">Edit Catalog Product</h3>
                  <button onClick={() => setEditProduct(null)} className="p-1 hover:bg-slate-700 rounded"><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {[
                    { key: 'name_en', label: 'Name (English)' },
                    { key: 'name_es', label: 'Name (Spanish)' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-medium text-slate-400 mb-1">{f.label}</label>
                      <input type="text" value={editForm[f.key]} onChange={e => setEditForm(v => ({ ...v, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Category (EN)</label>
                      <input type="text" value={editForm.category_en} onChange={e => setEditForm(v => ({ ...v, category_en: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Category (ES)</label>
                      <input type="text" value={editForm.category_es} onChange={e => setEditForm(v => ({ ...v, category_es: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Price (€)</label>
                      <input type="number" step="0.01" value={editForm.price} onChange={e => setEditForm(v => ({ ...v, price: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Cost Price</label>
                      <input type="number" step="0.01" value={editForm.cost_price} onChange={e => setEditForm(v => ({ ...v, cost_price: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">VAT</label>
                      <input type="text" value={editForm.tax_rate} onChange={e => setEditForm(v => ({ ...v, tax_rate: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Barcode</label>
                      <input type="text" value={editForm.barcode} onChange={e => setEditForm(v => ({ ...v, barcode: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Unit</label>
                      <input type="text" value={editForm.unit} onChange={e => setEditForm(v => ({ ...v, unit: e.target.value }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Image URL</label>
                    <input type="text" value={editForm.image_url} onChange={e => setEditForm(v => ({ ...v, image_url: e.target.value }))}
                      placeholder="/api/uploads/products/image.png or https://..."
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none" />
                    {editForm.image_url && (
                      <img src={editForm.image_url.startsWith('/api') ? `${API}${editForm.image_url}` : editForm.image_url}
                        alt="" className="w-12 h-12 object-contain mt-2 rounded bg-slate-700" />
                    )}
                  </div>
                </div>
                <div className="p-4 border-t border-slate-700 flex gap-3">
                  <button onClick={() => setEditProduct(null)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-600">Cancel</button>
                  <button onClick={saveEdit} disabled={saving}
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded text-sm hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="catalog-save-edit">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </SuperAdminLayout>
  );
};

export default SuperAdminCatalog;
